/*
 * The runtime of the public documentation pages (views/support/layouts/
 * docs_lean.php): everything assets/js/support/docs.js did with jQuery,
 * lc_lightbox, ClipboardJS and a Prism download build, in plain JavaScript.
 *
 *   - the side menu, page contents and page history panels (open, close)
 *   - the "On this page" list built from the headings, with scroll spy
 *   - heading anchors: copy the address of a heading
 *   - the search box (Ctrl+K), its results and the record of a chosen result
 *   - ?highlightText=...: marks the text on the page and scrolls to it
 *   - <tabset> blocks and .tab-link tabs
 *   - syntax highlighting and the copy button of code blocks
 *   - the image viewer of galleries
 *   - the group filter of the side menu
 */
import { highlightUnder } from './highlight.js';
import { initLightbox } from './lightbox.js';
import { copyText, initCopyCode } from './code_copy.js';

const runtime = globalThis.BARKODER_RUNTIME || {};
const HOME_URL = String(runtime.home_url || '/').replace(/\/?$/, '/');

const $ = (selector, root) => (root || document).querySelector(selector);
const $$ = (selector, root) => Array.from((root || document).querySelectorAll(selector));

/** Runs `handler(event, element)` for a click on, or inside, an element matching `selector`. */
function onClick(selector, handler) {
    document.addEventListener('click', (event) => {
        const el = event.target.closest ? event.target.closest(selector) : null;
        if (el) handler(event, el);
    });
}

function debounce(fn, wait) {
    let timer = null;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
    };
}

function pageAddress() {
    return window.location.href.split('#')[0];
}

/* ---------- <tabset> blocks from markdown ---------- */

let tabsetCount = 0;

// Turns <tabset><template v-slot:tabs>..<template v-slot:content>.. into the
// .tabs-component markup the tab clicks below work on.
function enhanceTabsets() {
    $$('tabset:not([data-enhanced])').forEach((tabset) => {
        try {
            const templates = $$('template', tabset);
            const named = (name) => templates.find((tpl) => tpl.getAttribute('v-slot') === name || tpl.hasAttribute('v-slot:' + name));
            const tabsTpl = named('tabs');
            const contentTpl = named('content');
            if (!tabsTpl || !contentTpl) return;

            const tabsBox = document.createElement('div');
            tabsBox.innerHTML = tabsTpl.innerHTML || '';
            const tabItems = $$('li', tabsBox);
            if (!tabItems.length) return;

            const contentBox = document.createElement('div');
            contentBox.innerHTML = contentTpl.innerHTML || '';
            const panels = $$('.tabset-panel', contentBox);
            if (!panels.length) return;

            const wrapper = document.createElement('div');
            wrapper.className = 'tabs-component';
            const tabsBar = document.createElement('div');
            tabsBar.className = 'tabs';
            const panelsWrap = document.createElement('div');
            panelsWrap.className = 'tabs-component-panels';

            const groupId = 'tabset-' + tabsetCount++;
            tabItems.forEach((item, idx) => {
                const tabId = groupId + '-tab' + idx;

                const tab = document.createElement('button');
                tab.type = 'button';
                tab.className = 'tab-link' + (idx === 0 ? ' active' : '');
                tab.setAttribute('data-tab', tabId);
                tab.innerHTML = item.innerHTML;
                tabsBar.appendChild(tab);

                const panel = document.createElement('div');
                panel.id = tabId;
                panel.className = 'tab-content' + (idx === 0 ? ' active' : '');
                panel.innerHTML = panels[idx] ? panels[idx].innerHTML : '';
                panelsWrap.appendChild(panel);
            });

            while (tabset.firstChild) tabset.removeChild(tabset.firstChild);
            wrapper.appendChild(tabsBar);
            wrapper.appendChild(panelsWrap);
            tabset.appendChild(wrapper);
            tabset.setAttribute('data-enhanced', '1');
        } catch (err) {
            // One broken tabset must not stop the page
        }
    });
}

function initTabs() {
    onClick('.tab-link', (event, tab) => {
        event.preventDefault();
        const tabId = tab.getAttribute('data-tab');
        // Only the tabs of this group change
        const wrap = tab.closest('.tabs-component') || tab.parentElement;
        if (!wrap) return;

        $$('.tab-link', wrap).forEach((el) => el.classList.remove('active'));
        $$('.tab-content', wrap).forEach((el) => el.classList.remove('active'));
        tab.classList.add('active');

        const panel = tabId ? $$('[id]', wrap).find((el) => el.id === tabId) : null;
        if (panel) {
            panel.classList.add('active');
            highlightUnder(panel);
        }
    });
}

/* ---------- Panels: side menu, page contents, page history ---------- */

function initPanels() {
    // Page contents, on a phone
    onClick('.pageContents > h4', (event) => {
        event.preventDefault();
        $$('.pageContents').forEach((el) => el.classList.toggle('open'));
    });

    // Side menu, on a phone
    onClick('header .hamburger', (event) => {
        event.preventDefault();
        $$('aside').forEach((el) => el.classList.toggle('open'));
        $$('.asideOverlay').forEach((el) => el.classList.toggle('open'));
    });
    onClick('.asideOverlay', (event) => {
        event.preventDefault();
        $$('aside').forEach((el) => el.classList.remove('open'));
        $$('.asideOverlay').forEach((el) => el.classList.remove('open'));
    });

    // The arrow of a menu entry opens and closes its children; the entry itself is a link
    $$('nav .link.active').forEach((el) => el.classList.add('showChilden'));
    onClick('nav .link svg', (event, svg) => {
        event.preventDefault();
        event.stopPropagation();
        const link = svg.closest('.link');
        if (link) link.classList.toggle('showChilden');
    });

    // Page history (staff only)
    onClick('.pageHistory .title a', (event) => {
        event.preventDefault();
        $$('.pageHistory').forEach((el) => el.classList.remove('open'));
    });
    onClick('.togglePageHistory', (event) => {
        event.preventDefault();
        $$('.pageHistory').forEach((el) => el.classList.toggle('open'));
    });

    // The current page of the side menu, in view
    const active = $('aside nav .active');
    if (active) active.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
}

/* ---------- "On this page": the list of headings, and scroll spy ---------- */

let spyOff = false;
let spyOffTimer = null;

function markContentLink(id) {
    const link = $$('.links .link').find((el) => el.getAttribute('data-id') === id);
    if (!link) return;
    $$('.links .link.active').forEach((el) => el.classList.remove('active'));
    link.classList.add('active');
}

function scrollToSection(id) {
    const sections = $$('.template_sections').filter((el) => el.id === id);
    if (sections.length !== 1) return;

    // The spy rests while the page scrolls there
    spyOff = true;
    clearTimeout(spyOffTimer);

    const admin = !!$('.admin_controls');
    let offset = admin ? 148 : 100;
    if (window.innerWidth <= 998) offset = admin ? 460 : 420;

    markContentLink(id);
    window.scrollTo({ top: sections[0].getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' });

    spyOffTimer = setTimeout(() => {
        spyOff = false;
    }, 1000);
}

function setAddress(url) {
    if (window.history && window.history.pushState) window.history.pushState(null, '', url);
}

// Gives every heading of the content an id made of its text, and the address to its "#" link.
function readHeadings() {
    const levels = ['h2', 'h3', 'h4', 'h5', 'h6'];
    return $$('.docsContent h2, .docsContent h3, .docsContent h4, .docsContent h5, .docsContent h6').map((el) => {
        const text = el.innerText || el.textContent || '';
        const id = text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        el.setAttribute('id', id);

        const url = pageAddress() + '#' + id;
        $$('a.locCopy', el).forEach((a) => a.setAttribute('href', url));

        return { id, text, level: levels.indexOf(el.tagName.toLowerCase()) };
    });
}

// The headings as nested .links lists: a deeper heading goes under the one before it.
function buildTree(headings) {
    if (!headings.length) return null;

    const box = document.createElement('div');
    box.className = 'links';

    let deeper = [];
    let lastLevel = -1;
    let lastLink = null;
    const flush = () => {
        // As docs.js had it: the sub-list is a sibling right after its heading's link
        const sub = deeper.length ? buildTree(deeper) : null;
        if (sub && lastLink) lastLink.after(sub);
        deeper = [];
    };

    headings.forEach((heading) => {
        if (lastLevel !== -1 && lastLevel < heading.level) {
            deeper.push(heading);
            return;
        }
        flush();
        lastLevel = heading.level;

        lastLink = document.createElement('div');
        lastLink.className = 'link';
        lastLink.setAttribute('data-lv', heading.level);
        lastLink.setAttribute('data-id', heading.id);
        lastLink.textContent = heading.text;
        box.appendChild(lastLink);
    });
    flush();

    return box;
}

function initPageContents() {
    const tree = buildTree(readHeadings());
    const inner = $('.pageContents > .inner');
    if (inner) {
        inner.textContent = '';
        if (tree) inner.appendChild(tree);
    }

    onClick('.pageContents .links > .link', (event, link) => {
        event.preventDefault();
        const id = link.getAttribute('data-id');
        scrollToSection(id);
        $$('.pageContents').forEach((el) => el.classList.remove('open'));
        setAddress(pageAddress() + '#' + id);
    });

    // The "#" beside a heading: the address of the heading, in the address bar and on the clipboard
    onClick('.locCopy', (event, link) => {
        event.preventDefault();
        const url = link.href;
        setAddress(url);
        copyText(url);
    });

    // Opened with #heading
    if (window.location.hash.length > 1) {
        let id = window.location.hash.slice(1);
        try {
            id = decodeURIComponent(id);
        } catch (e) {
            // Left as it is
        }
        if (document.getElementById(id)) scrollToSection(id);
    }
}

// Marks the heading in view in the "On this page" list while the page scrolls.
function initScrollSpy() {
    const sections = $$('.template_sections');
    let current = null;
    let ticking = false;
    let top = 0;
    let bottom = 0;

    const measure = () => {
        top = window.innerHeight * 0.01;
        bottom = window.innerHeight * 0.99;
    };

    const run = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
            const inView = sections.find((section) => {
                const rect = section.getBoundingClientRect();
                return rect.top >= top && rect.top + rect.height <= bottom;
            });
            if (inView && inView !== current) {
                current = inView;
                if (!spyOff) markContentLink(inView.getAttribute('id'));
            }
            ticking = false;
        });
    };

    measure();
    window.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', () => {
        measure();
        run();
    });
    run();
}

/* ---------- ?highlightText=... (a search result opens the page with it) ---------- */

function markText(searchText) {
    const content = $('.docsContent');
    if (!content || !searchText) return;

    const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
            // Code stays as it is
            if (node.parentElement && node.parentElement.closest('pre, code, .codeBlock')) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        },
    });

    // The nodes first: replacing them while walking would lose the walker's place
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    let first = null;
    let count = 0;
    const limit = 500;

    nodes.forEach((node) => {
        if (count > limit) return;
        const text = node.nodeValue;
        regex.lastIndex = 0;
        if (!regex.test(text)) return;
        regex.lastIndex = 0;

        const pieces = document.createDocumentFragment();
        let last = 0;
        let match;
        while ((match = regex.exec(text)) && count <= limit) {
            if (match[0] === '') {
                regex.lastIndex++;
                continue;
            }
            if (match.index > last) pieces.appendChild(document.createTextNode(text.slice(last, match.index)));
            const mark = document.createElement('mark');
            mark.textContent = match[0];
            pieces.appendChild(mark);
            if (!first) first = mark;
            last = match.index + match[0].length;
            count++;
        }
        if (last < text.length) pieces.appendChild(document.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(pieces, node);
    });

    if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
}

function initHighlightText() {
    let text = '';
    try {
        text = new URLSearchParams(window.location.search).get('highlightText') || '';
    } catch (e) {
        text = '';
    }
    if (!text) return;
    setTimeout(() => {
        try {
            markText(text);
        } catch (e) {
            // The page reads the same without the marks
        }
    }, 200);
}

/* ---------- Search ---------- */

function initSearch() {
    const box = $('.searchBox');
    if (!box) return;

    const overlay = $('.searchBox-overlay');
    const input = $('.searchInput input', box);
    const clear = $('.searchInput button', box);
    const results = $('.searchResults', box);
    // "0" means none, as it did before
    const dataOf = (name) => {
        const value = box.getAttribute(name);
        return value && value !== '0' ? value : '';
    };
    let asked = 0;

    const toggle = () => {
        box.classList.toggle('open');
        if (overlay) overlay.classList.toggle('open');
        setTimeout(() => {
            if (input && box.offsetParent !== null) input.focus();
        }, 100);
    };

    onClick('header .search button, header .search svg', (event) => {
        event.preventDefault();
        toggle();
    });
    onClick('.searchBox-overlay', (event) => {
        event.preventDefault();
        box.classList.remove('open');
        if (overlay) overlay.classList.remove('open');
    });

    const close = () => {
        box.classList.remove('open');
        if (overlay) overlay.classList.remove('open');
    };
    onClick('.searchBox .searchClose', (event) => {
        event.preventDefault();
        close();
    });

    document.addEventListener('keydown', (event) => {
        if (event.ctrlKey && (event.key === 'k' || event.key === 'K' || event.keyCode === 75)) {
            event.preventDefault();
            toggle();
        }
        if (event.key === 'Escape' && box.classList.contains('open')) close();
    });

    const search = () => {
        const q = input ? input.value : '';
        if (clear) clear.classList.toggle('visible', !!q);

        const query = new URLSearchParams({
            q,
            context_page_id: dataOf('data-context-page-id'),
            version_id: dataOf('data-version-id'),
            num_show: '50',
        });
        const mine = ++asked;

        fetch(HOME_URL + 'docs/ajax/searchDocs?' + query.toString(), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
            .then((response) => {
                if (!response.ok) throw new Error('error, ' + (response.statusText || response.status));
                return response.text();
            })
            .then((html) => {
                // An answer to an older question does not replace a newer one
                if (mine === asked && results) results.innerHTML = html;
            })
            .catch((error) => {
                if (mine === asked && results) results.textContent = 'Search request failed: ' + ((error && error.message) || 'error');
            });
    };

    if (input) input.addEventListener('keyup', debounce(search, 300));
    if (clear) {
        clear.addEventListener('click', () => {
            if (input) input.value = '';
            clear.classList.remove('visible');
            search();
        });
    }

    // Which result was chosen for which words: the server learns from it
    if (results) {
        results.addEventListener('click', (event) => {
            const link = event.target.closest ? event.target.closest('a.resultPage') : null;
            if (!link) return;
            try {
                const pageId = link.getAttribute('data-page-id');
                const q = (input && input.value) || '';
                if (!pageId || pageId === '0' || !q) return;

                const numeric = (value) => (/^\d+$/.test(value) ? Number(value) : value);
                const body = JSON.stringify({ q, version_id: numeric(dataOf('data-version-id')), page_id: numeric(pageId) });
                const url = HOME_URL + 'docs/ajax/saveSearchHit';

                if (navigator.sendBeacon) {
                    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
                } else {
                    fetch(url, {
                        method: 'POST',
                        credentials: 'same-origin',
                        keepalive: true,
                        headers: { 'Content-Type': 'application/json; charset=UTF-8', 'X-Requested-With': 'XMLHttpRequest' },
                        body,
                    }).catch(() => {});
                }
            } catch (e) {
                // The link opens all the same
            }
        });
    }
}

/* ---------- Code blocks: the copy button ---------- */

function initCopyCodeBlocks() {
    initCopyCode('.docsContent .codeBlock > button');
}

/* ---------- The group filter of the side menu ---------- */

function initGroupFilter() {
    onClick('.filterDropdown .filterToggle', (event, toggle) => {
        event.preventDefault();
        const dropdown = toggle.closest('.filterDropdown');
        if (dropdown) dropdown.classList.toggle('open');
    });

    // A click anywhere else closes it
    document.addEventListener('click', (event) => {
        if (event.target.closest && event.target.closest('.filterDropdown')) return;
        $$('.filterDropdown').forEach((el) => el.classList.remove('open'));
    });

    // The native select (the dropdown's fallback) opens the group's first page
    document.addEventListener('change', (event) => {
        const select = event.target;
        if (!select || select.id !== 'groupSelect' || !select.classList.contains('native-filter')) return;

        const group = select.value || '';
        if (!group) {
            window.location.href = window.location.pathname.split('?')[0];
            return;
        }
        const path = group.replace(/^\/+/, '');
        window.location.href = (select.getAttribute('data-docs-base') || '') + path + '?g=' + encodeURIComponent('/' + path);
    });
}

/* ---------- Start ---------- */

// Each part on its own: one that fails does not take the others with it.
function part(fn) {
    try {
        fn();
    } catch (e) {
        if (window.console && console.error) console.error(e);
    }
}

function start() {
    part(enhanceTabsets);
    part(() => highlightUnder(document));
    part(initLightbox);
    part(initHighlightText);
    part(initPanels);
    part(initPageContents);
    part(initSearch);
    part(initTabs);
    part(initCopyCodeBlocks);
    part(initGroupFilter);
    // After the first layout has settled, as before
    setTimeout(() => part(initScrollSpy), 400);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
